import { helpPath } from './helpNavigation.mjs';
import useHelpLanguage from './useHelpLanguage.jsx';
import React from "react";
import { Link } from "react-router-dom";
import * as Icons from "@/icons";
import Button from "@/components/ui/Button.jsx";
import SettingsCard from "@/sot/primitives/SettingsCard.jsx";

function HelpMetaChip({ children }) {
  return (
    <span className="inline-flex min-w-0 items-center rounded-[8px] border border-[rgba(29,79,215,0.16)] bg-[rgba(77,163,255,0.12)] px-3 py-1 text-label-medium text-on-surface">
      {children}
    </span>
  );
}

export function HelpSection({ eyebrow, title, description, children, action = null }) {
  return (
    <section className="w-full min-w-0 space-y-3 overflow-hidden">
      <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0">
          {eyebrow ? (
            <div className="mb-2 text-label-large text-[rgb(var(--afrimed-primary))]">
              {eyebrow}
            </div>
          ) : null}
          <h2 className="break-words text-title-large text-on-surface">{title}</h2>
          {description ? (
            <p className="mt-2 max-w-[720px] break-words text-body-large text-on-surface-var">
              {description}
            </p>
          ) : null}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

export function HelpSearchBar({ value, onChange, placeholder = "Search help" }) {
  return (
    <label className="relative block">
      <span className="sr-only">{placeholder}</span>
      <div className="flex h-14 w-full items-center rounded-[28px] bg-[rgb(var(--clr-surface-cont-highest))] p-1 shadow-[0px_0px_0px_1px_rgba(24,50,119,0.10),0px_1.5px_0px_-0.5px_rgba(26,27,35,0.06),0px_3px_3px_-1px_rgba(26,27,35,0.06),0px_3px_6px_-2px_rgba(26,27,35,0.06),0px_6px_12px_-4px_rgba(29,79,215,0.06),0px_12px_24px_-10px_rgba(29,79,215,0.06)] focus-within:ring-2 focus-within:ring-[rgba(29,78,216,0.14)]">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[12px]">
          <Icons.Search className="h-6 w-6 text-on-surface-var" aria-hidden />
        </div>
        <input
          value={value}
          onChange={(event) => onChange?.(event.target.value)}
          placeholder={placeholder}
          className="h-full min-w-0 flex-1 bg-transparent pr-4 text-body-large text-on-surface outline-none placeholder:text-on-surface-var"
        />
      </div>
    </label>
  );
}

export function HelpCategoryCard({ category, compact = false, basePath = "/help", isFrench = false }) {
  const { languageCode } = useHelpLanguage();
  const Icon = Icons[category.icon] || Icons.HelpCircle;
  const readyCount = category.articles?.length || 0;
  const guideLabel = isFrench
    ? `${readyCount} guide${readyCount > 1 ? "s" : ""}`
    : `${readyCount} guide${readyCount === 1 ? "" : "s"}`;

  return (
    <Link
      to={helpPath(basePath, category.slug, "", category.audience, languageCode)}
      className="group flex h-full w-full min-w-0 flex-col rounded-[14px] focus:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(29,78,216,0.24)]"
    >
      <div className="mb-1 flex min-h-[40px] min-w-0 items-start gap-3 px-1">
        <Icon className="h-5 w-5 shrink-0 text-on-surface-var" aria-hidden />
        <h3 className="min-w-0 break-words text-title-medium text-on-surface line-clamp-2">{category.title}</h3>
      </div>
      <SettingsCard fullWidth className="flex min-h-[148px] min-w-0 flex-1 flex-col justify-between px-4 py-4 shadow-[var(--elev-1)] md:min-h-[156px] md:px-5">
        <div className="flex min-w-0 items-start justify-between gap-3">
          <p className={`min-w-0 flex-1 break-words text-body-medium text-on-surface-var ${compact ? "line-clamp-2" : "line-clamp-3"}`}>
            {category.summary}
          </p>
          <Icons.ChevronRight className="mt-0.5 h-5 w-5 shrink-0 text-on-surface-var" />
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <HelpMetaChip>{readyCount ? guideLabel : category.status}</HelpMetaChip>
          {!readyCount ? (
            <HelpMetaChip>{isFrench ? "Prêt pour captures" : "Screenshot-ready"}</HelpMetaChip>
          ) : null}
        </div>
      </SettingsCard>
    </Link>
  );
}

export function HelpArticleCard({ article, basePath = "/help" }) {
  const { languageCode } = useHelpLanguage();
  return (
    <Link
      to={helpPath(basePath, article.categorySlug, article.slug, article.audience, languageCode)}
      className="group flex h-full w-full min-w-0 flex-col rounded-[14px] focus:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(29,78,216,0.24)]"
    >
      <div className="mb-1 min-h-[52px] min-w-0 px-1 md:min-h-[56px]">
        <div className="text-label-medium text-[rgb(var(--afrimed-primary))]">
          {article.categoryTitle}
        </div>
        <h3 className="mt-1 break-words text-title-medium text-on-surface line-clamp-2">{article.title}</h3>
      </div>
      <SettingsCard fullWidth className="flex min-h-[88px] min-w-0 flex-1 px-4 py-3.5 shadow-[var(--elev-1)] md:px-5">
        <div className="flex min-w-0 items-start justify-between gap-4">
          <p className="min-w-0 flex-1 break-words text-body-medium text-on-surface-var line-clamp-3">{article.summary}</p>
          <Icons.ChevronRight className="mt-2 h-5 w-5 shrink-0 text-on-surface-var" />
        </div>
      </SettingsCard>
    </Link>
  );
}

export function HelpMobileSectionNav({ categories, activeSlug, basePath = "/help", isFrench = false }) {
  const { languageCode } = useHelpLanguage();
  const activeRef = React.useRef(null);

  React.useEffect(() => {
    activeRef.current?.scrollIntoView?.({
      block: "nearest",
      inline: "nearest",
    });
  }, [activeSlug]);

  return (
    <div className="w-full min-w-0 overflow-hidden md:hidden">
      <nav
        className="w-full overflow-x-auto overscroll-x-contain pb-1 [-webkit-overflow-scrolling:touch]"
        aria-label={isFrench ? "Sections d’aide" : "Help sections"}
      >
        <div className="flex min-w-max gap-3">
          {categories.map((category) => {
            const active = category.slug === activeSlug;
            return (
              <Link
                key={category.slug}
                ref={active ? activeRef : null}
                to={helpPath(basePath, category.slug, "", category.audience, languageCode)}
                aria-current={active ? "page" : undefined}
                className={[
                  "shrink-0 whitespace-nowrap rounded-[12px] px-1 py-2 text-label-large focus:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(29,78,216,0.24)]",
                  active
                    ? "text-[rgb(var(--afrimed-primary))]"
                    : "text-on-surface-var",
                ].join(" ")}
              >
                {category.title}
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
}

export function HelpSidebar({ categories, activeSlug, basePath = "/help", isFrench = false }) {
  const { languageCode } = useHelpLanguage();
  const label = isFrench ? "Sections d’aide" : "Help sections";
  return (
    <SettingsCard className="hidden px-0 py-0 shadow-[var(--elev-1)] lg:block">
      <div className="px-4 pb-2 pt-4 text-title-small text-on-surface-var">{label}</div>
      <nav aria-label={label}>
        {categories.map((category) => {
          const active = category.slug === activeSlug;
          const isLast = category.slug === categories[categories.length - 1]?.slug;
          return (
            <div key={category.slug}>
              <Link
                to={helpPath(basePath, category.slug, "", category.audience, languageCode)}
                className={[
                  "mx-3 flex min-h-[52px] items-center justify-between gap-3 rounded-[12px] px-3 text-body-large focus:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(29,78,216,0.24)]",
                  active
                    ? "text-[rgb(var(--afrimed-primary))]"
                    : "text-on-surface-var",
                ].join(" ")}
              >
                <span className="min-w-0 break-words">{category.title}</span>
                <Icons.ChevronRight className="h-5 w-5 shrink-0" aria-hidden />
              </Link>
              {!isLast ? (
                <div
                  className="h-px"
                  style={{
                    marginLeft: 16,
                    background: "var(--Schemes-Outline-Variant,#E6E7EB)",
                  }}
                />
              ) : null}
            </div>
          );
        })}
      </nav>
    </SettingsCard>
  );
}

export function HelpStepList({ steps = [] }) {
  return (
    <ol>
      {steps.map((step, index) => (
        <li key={`${index}-${step}`}>
          <div className="flex min-h-[60px] gap-3 py-3.5 md:min-h-[56px] md:py-3">
            <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[rgba(29,78,216,0.10)] text-label-large text-[rgb(var(--afrimed-primary))]">
              {index + 1}
            </span>
            <span className="min-w-0 break-words text-body-large text-on-surface-var">{step}</span>
          </div>
          {index !== steps.length - 1 ? (
            <div
              className="h-px"
              style={{
                marginLeft: 40,
                background: "var(--Schemes-Outline-Variant,#E6E7EB)",
              }}
            />
          ) : null}
        </li>
      ))}
    </ol>
  );
}

export function HelpCallout({ callout }) {
  if (!callout) return null;

  const tone = {
    info: "border-[rgba(29,78,216,0.35)] bg-[rgba(29,78,216,0.08)] text-[rgb(var(--afrimed-primary))]",
    trust: "border-[rgba(29,79,215,0.16)] bg-[rgba(77,163,255,0.12)] text-[rgb(var(--afrimed-primary))]",
    warning: "border-[rgba(var(--schemes-critical),0.24)] bg-[var(--state-critical-08)] text-[rgb(var(--schemes-critical))]",
    critical: "border-[rgba(var(--schemes-critical),0.24)] bg-[var(--state-critical-08)] text-[rgb(var(--schemes-critical))]",
  }[callout.tone || "info"];

  const Icon =
    callout.tone === "warning" || callout.tone === "critical"
      ? Icons.ShieldAlert
      : callout.tone === "trust"
        ? Icons.ShieldCheck
        : Icons.Info;

  return (
    <div className={`w-full min-w-0 rounded-[12px] border px-4 py-[18px] md:py-4 ${tone}`}>
      <div className="flex min-w-0 gap-3">
        <Icon className="mt-0.5 h-5 w-5 shrink-0" aria-hidden />
        <div className="min-w-0">
          <div className="break-words text-title-small">{callout.title}</div>
          <p className="mt-1 break-words text-body-medium text-on-surface-var">{callout.body}</p>
        </div>
      </div>
    </div>
  );
}

export function HelpScreenshotFrame({ screenshot }) {
  if (!screenshot) return null;

  if (screenshot.src) {
    return (
      <figure className="w-full min-w-0 overflow-hidden rounded-[14px] border border-[rgba(91,107,134,0.18)] bg-[rgb(var(--clr-surface-cont-highest))] p-3.5 md:p-3">
        <img
          src={screenshot.src}
          alt={screenshot.alt || screenshot.label || ""}
          loading="lazy"
          decoding="async"
          className="h-auto w-full rounded-[10px] object-contain"
        />
        {(screenshot.label || screenshot.caption) ? (
          <figcaption className="px-1 pb-1 pt-3.5 md:pt-3">
            {screenshot.label ? (
              <div className="break-words text-title-small text-on-surface">{screenshot.label}</div>
            ) : null}
            {screenshot.caption ? (
              <p className="mt-1 break-words text-body-medium text-on-surface-var">
                {screenshot.caption}
              </p>
            ) : null}
          </figcaption>
        ) : null}
      </figure>
    );
  }

  return (
    <div className="w-full min-w-0 overflow-hidden rounded-[14px] border border-dashed border-[rgba(91,107,134,0.24)] bg-[rgb(var(--clr-surface-cont-highest))] p-4">
      <div className="flex min-h-[168px] items-center justify-center">
        <div className="px-4 text-center">
          <Icons.Image className="mx-auto h-8 w-8 text-on-surface-var" aria-hidden />
          <div className="mt-3 break-words text-title-small text-on-surface">{screenshot.label}</div>
          <p className="mt-1 max-w-[360px] break-words text-body-medium text-on-surface-var">
            {screenshot.caption}
          </p>
        </div>
      </div>
    </div>
  );
}

export function HelpEmptyState({ title, body, action = null }) {
  return (
    <SettingsCard fullWidth className="min-w-0 px-5 py-8 text-center">
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-[18px] bg-[rgba(29,78,216,0.10)] text-[rgb(var(--afrimed-primary))]">
        <Icons.BookOpen className="h-7 w-7" aria-hidden />
      </div>
      <h3 className="mt-4 break-words text-title-large text-on-surface">{title}</h3>
      <p className="mx-auto mt-2 max-w-[520px] break-words text-body-large text-on-surface-var">{body}</p>
      {action ? <div className="mt-5">{action}</div> : null}
    </SettingsCard>
  );
}

export function HelpSupportActionBlock({ onBack, supportTo = "/help-support", isFrench = false }) {
  return (
    <section className="w-full min-w-0 px-4 py-2 md:px-0">
      <div className="min-w-0">
        <h2 className="break-words text-title-large text-on-surface">
          {isFrench ? "Besoin d’aide supplémentaire ?" : "Need more help?"}
        </h2>
        <p className="mt-1 max-w-[760px] break-words text-body-medium text-on-surface-var">
          {isFrench
            ? "Ouvrez une demande d’assistance si le guide ne correspond pas à ce que vous voyez dans AfriMed™."
            : "Open a support request if the guide does not match what you see in AfriMed™."}
        </p>
      </div>
      <div className="mt-4 grid w-full min-w-0 gap-2 sm:flex sm:flex-wrap">
        <Button
          variant="ghost"
          className="h-11 w-full sm:w-[180px]"
          iconName="ArrowLeft"
          iconPosition="left"
          onClick={onBack}
        >
          {isFrench ? "Retour à la section" : "Back to section"}
        </Button>
        <Link to={supportTo} className="block w-full min-w-0 sm:w-[180px]">
          <Button
            variant="tonal"
            className="h-11 w-full"
            iconName="MessageSquarePlus"
            iconPosition="left"
          >
            {isFrench ? "Contacter l’assistance" : "Contact support"}
          </Button>
        </Link>
      </div>
    </section>
  );
}
